<script setup lang="ts">
import CreateButton from "@/components/shared/CreateButton.vue";
import DeleteButton from "@/components/shared/DeleteButton.vue";
import {
  computed,
  onScopeDispose,
  ref,
  shallowRef,
  toRaw,
  triggerRef,
  watch,
} from "vue";
import GeneratedCrudForm from "@/components/generated/GeneratedCrudForm.vue";
import GeneratedFieldControl from "@/components/generated/GeneratedFieldControl.vue";
import MemberProfileContacts from "./MemberProfileContacts.vue";
import {
  errorMessage,
  validationFieldErrors,
  type FieldLayoutGroup,
  type FieldOverrides,
} from "@/composables/generated/contracts";
import { emptySearchParams } from "@/composables/useTableHelper";
import { useAuthStore } from "@/stores/auth";
import { PersonMemberAccessor } from "../../../cbi-generator/frontend/accessors/PersonMemberAccessor";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import { usePersonTypeRulesService } from "../../../cbi-generator/frontend/composables/usePersonTypeRulesFrontendService";
import { CreatePersonMemberMeta } from "../../../cbi-generator/generated/dto/CreatePersonMemberMeta";
import { UpdatePersonMemberMeta } from "../../../cbi-generator/generated/dto/UpdatePersonMemberMeta";
import { PersonTypeKey } from "../../../cbi-generator/generated/dto/PersonTypeKey";
import { CountryCode } from "../../../cbi-generator/generated/dto/CountryCode";
import { USStateCode } from "../../../cbi-generator/generated/dto/USStateCode";
import type { PersonTypeMask } from "../../../cbi-generator/generated/dto/PersonTypeMask";
import type { MemberProfile } from "../../../cbi-generator/generated/dto/MemberProfile";
import type { MemberProfileUpdate } from "../../../cbi-generator/generated/dto/MemberProfileUpdate";
import type { MemberProfilePhoneChange } from "../../../cbi-generator/generated/dto/MemberProfilePhoneChange";
import type { MemberProfileAddressChange } from "../../../cbi-generator/generated/dto/MemberProfileAddressChange";
import type { MemberEmergencyContactCreate } from "../../../cbi-generator/generated/dto/MemberEmergencyContactCreate";
import type { MemberEmergencyContactUpdate } from "../../../cbi-generator/generated/dto/MemberEmergencyContactUpdate";
import type { PersonMember } from "../../../cbi-generator/generated/dto/PersonMember";
import {
  fieldLabel,
  readField,
  writeField,
} from "@/composables/generated/fields";

const props = withDefaults(
  defineProps<{
    personId?: number;
    child?: boolean;
    registration?: boolean;
    email?: string;
    disabled?: boolean;
    submitLabel?: string;
    minEmergencyContacts?: number;
    /** Validate against this person type instead of the stored one. */
    personType?: PersonTypeKey;
    /** Split the form into the legacy person-editor tabs. */
    tabbed?: boolean;
    /** Shown (and blocks submit) when the date of birth is below the mask's minimum age. */
    underAgeMessage?: string;
  }>(),
  { submitLabel: "Save", minEmergencyContacts: 0 },
);
const emit = defineEmits<{ saved: [profile: MemberProfile]; cancel: [] }>();
const service = usePersonService();
const rules = usePersonTypeRulesService();
const auth = useAuthStore();
const accessor = shallowRef<PersonMemberAccessor>();
const original = shallowRef<MemberProfile>();
const masks = shallowRef<PersonTypeMask[]>([]);
const phones = ref<MemberProfilePhoneChange[]>([]);
const addresses = ref<MemberProfileAddressChange[]>([]);
const newContacts = ref<MemberEmergencyContactCreate[]>([]);
const contacts = shallowRef<PersonMember[]>([]);
const selectedContacts = ref<number[]>([]);
type ContactDraft = {
  person: { first: string | null; last: string | null; email: string | null };
  phones: MemberProfilePhoneChange[];
  addresses: MemberProfileAddressChange[];
  editableInPlace: boolean;
};
const contactDrafts = ref<Record<number, ContactDraft>>({});
const household = shallowRef<PersonMember[]>([]);
type HouseholdAddress = {
  name: string;
  address: NonNullable<MemberProfileAddressChange["address"]>;
};
const householdAddresses = shallowRef<HouseholdAddress[]>([]);
const loading = ref(false);
const saving = ref(false);
const loadError = ref("");
const saveError = ref("");
const errors = ref<Record<string, string>>({});
let version = 0;
onScopeDispose(() => ++version);
const blocked = computed(() => props.disabled || loading.value || saving.value);
const selectedTargetType = ref<string | null>(null);

const mask = computed(() =>
  masks.value.find(
    (m) =>
      m.personType ===
      (selectedTargetType.value ??
        props.personType ??
        (props.child
          ? (original.value?.personType ?? PersonTypeKey.JUNIOR_MEMBER)
          : (original.value?.personType ?? PersonTypeKey.FRIEND))),
  ),
);

// GeneratedCrudForm edits don't re-trigger `accessor`, so the date of birth is mirrored here.
const dobDraft = ref<Date | null>(null);
watch(accessor, (a) => (dobDraft.value = a?.fields.dob ?? null), {
  immediate: true,
});
const underAge = computed(() => {
  const minAge = mask.value?.minAge;
  const dob = dobDraft.value;
  if (!props.underAgeMessage || minAge == null || !dob) return false;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
  )
    age--;
  return age < minAge;
});

const PERSON_TYPE_LABELS: Record<string, string> = {
  [PersonTypeKey.ADULT_MEMBER]: "Adult Member",
  [PersonTypeKey.JUNIOR_MEMBER]: "Junior Member",
  [PersonTypeKey.UAP_MEMBER]: "UAP Member",
  [PersonTypeKey.ORGANIZATION]: "Organization",
  [PersonTypeKey.ORGANIZATION_CONTACT]: "Organization Contact",
  [PersonTypeKey.FRIEND]: "Friend",
  [PersonTypeKey.JUNIOR_PARENT]: "Junior Parent",
  [PersonTypeKey.ADULT_GUEST]: "Adult Guest",
  [PersonTypeKey.JP_INSTRUCTOR]: "JP Instructor",
  [PersonTypeKey.EMERGENCY_CONTACT]: "Emergency Contact",
};

const DISPLAYED_ELIGIBLE_TYPES = new Set<string>([
  PersonTypeKey.ADULT_MEMBER,
  PersonTypeKey.JUNIOR_PARENT,
]);
const displayedMasks = computed(() =>
  masks.value.filter((m) => DISPLAYED_ELIGIBLE_TYPES.has(m.personType)),
);

const assignedPersonTypes = computed<string[]>(() => {
  const result = new Set<string>();
  if (original.value?.personType) {
    result.add(original.value.personType);
  }
  const pluralList = original.value?.person?.personTypes ?? [];
  for (const pt of pluralList) {
    const typeId = pt.typeIdTargetIds?.typeId;
    if (typeId != null) {
      const match = masks.value.find((m) => m.personTypeId?.typeId === typeId);
      if (match?.personType) {
        result.add(match.personType);
      }
    }
  }
  return Array.from(result);
});

const eligiblePersonTypes = computed<string[]>(() => {
  if (!accessor.value || !masks.value.length) return [];
  const formValues = accessor.value.fields;
  const addressCount = addresses.value.filter((a) => !a.remove).length;
  const phoneCount = phones.value.filter((p) => !p.remove).length;
  const emergencyCount =
    selectedContacts.value.length + newContacts.value.length;

  return displayedMasks.value
    .filter((m) => {
      if (addressCount < m.minAddresses) return false;
      if (phoneCount < m.minPhones) return false;
      if (emergencyCount < m.minEmergencyContacts) return false;
      for (const field of m.requiredFields) {
        const val = formValues[field as keyof typeof formValues];
        if (field === "dob") {
          if (!val) return false;
        } else if (typeof val === "string") {
          if (!val.trim()) return false;
        } else if (val == null) {
          return false;
        }
      }
      return true;
    })
    .map((m) => m.personType);
});

function selectTargetType(typeKey: string) {
  if (selectedTargetType.value === typeKey) {
    selectedTargetType.value = null;
  } else {
    selectedTargetType.value = typeKey;
    validateTargetRole(typeKey);
  }
}

function validateTargetRole(typeKey: string) {
  const targetMask = masks.value.find((m) => m.personType === typeKey);
  if (!targetMask || !accessor.value) return;
  const formValues = accessor.value.fields;
  const missingFieldErrors: Record<string, string> = { ...errors.value };

  for (const field of targetMask.requiredFields) {
    const val = formValues[field as keyof typeof formValues];
    const isBlank =
      field === "dob"
        ? !val
        : typeof val === "string"
          ? !val.trim()
          : val == null;
    if (isBlank) {
      const label = labels[field] ?? field;
      missingFieldErrors[field] =
        `${label} is required for ${PERSON_TYPE_LABELS[typeKey] ?? typeKey}.`;
    } else {
      delete missingFieldErrors[field];
    }
  }

  const addressCount = addresses.value.filter((a) => !a.remove).length;
  if (addressCount < targetMask.minAddresses) {
    missingFieldErrors.addresses = `At least ${targetMask.minAddresses} address is required for ${PERSON_TYPE_LABELS[typeKey] ?? typeKey}.`;
  } else {
    delete missingFieldErrors.addresses;
  }

  const phoneCount = phones.value.filter((p) => !p.remove).length;
  if (phoneCount < targetMask.minPhones) {
    missingFieldErrors.phones = `At least ${targetMask.minPhones} phone number is required for ${PERSON_TYPE_LABELS[typeKey] ?? typeKey}.`;
  } else {
    delete missingFieldErrors.phones;
  }

  const emergencyCount =
    selectedContacts.value.length + newContacts.value.length;
  if (emergencyCount < targetMask.minEmergencyContacts) {
    missingFieldErrors.emergencyContacts = `At least ${targetMask.minEmergencyContacts} emergency contacts required for ${PERSON_TYPE_LABELS[typeKey] ?? typeKey}.`;
  } else {
    delete missingFieldErrors.emergencyContacts;
  }

  errors.value = missingFieldErrors;
  accessor.value.fieldErrors = missingFieldErrors;
  triggerRef(accessor);
}
const layout: FieldLayoutGroup[] = [
  {
    key: "name",
    title: "Name",
    fields: ["prefix", "first", "middle", "last", "suffix", "preferred"],
  },
  { key: "contact", title: "Contact information", fields: ["email", "dob"] },
  {
    key: "demographics",
    title: "Demographics",
    fields: ["gender", "ethnicity", "language", "school"],
  },
  {
    key: "experience",
    title: "Experience",
    fields: ["swimProof", "sailingExp"],
  },
  {
    key: "specialNeeds",
    title: "Special needs",
    fields: ["allergies", "medications", "specialNeeds"],
  },
  {
    key: "employment",
    title: "Employment",
    fields: ["employer", "occupation", "matchingGifts"],
  },
];
// Tabbed mode mirrors the legacy editor: name and demographics share one tab,
// and experience, special needs and employment are omitted.
const tabLayout: FieldLayoutGroup[] = [
  layout[0],
  { key: "details", fields: ["organization", "dob"] },
  layout[2],
];
const tabFields = tabLayout.flatMap((group) => group.fields);
type ProfileTab = "info" | "contact" | "address" | "emergency";
const tab = ref<ProfileTab>("info");
const tabTitle = computed(() =>
  props.personId != null &&
  String(props.personId) === auth.accessToken?.claims.sub
    ? "My Information"
    : "Information",
);
const showAddressTab = computed(
  () =>
    !!mask.value?.visibleSections.includes("address") ||
    (mask.value?.minAddresses ?? 0) > 0,
);
const showEmergencyTab = computed(
  () =>
    !!mask.value?.visibleSections.includes("emergencyContacts") ||
    minimumContacts.value > 0,
);
function errorTab(key: string): ProfileTab {
  if (key === "email" || key.startsWith("phones")) return "contact";
  if (key.startsWith("addresses")) return "address";
  if (
    key.startsWith("emergencyContacts") ||
    key.startsWith("newEmergency") ||
    key.startsWith("updatedEmergency")
  )
    return "emergency";
  return "info";
}
const tabErrors = computed(() => {
  const counts: Partial<Record<ProfileTab, number>> = {};
  for (const key of Object.keys(errors.value))
    counts[errorTab(key)] = (counts[errorTab(key)] ?? 0) + 1;
  return counts;
});
const labels: Record<string, string> = {
  first: "First name",
  last: "Last name",
  middle: "Middle name",
  preferred: "Preferred name",
  dob: "Date of birth",
  sailingExp: "Sailing experience",
  swimProof: "Swim proof",
  specialNeeds: "Special needs",
  matchingGifts: "Matching gifts",
};
const fieldGroups: Record<string, string> = {
  first: "name",
  last: "name",
  preferred: "name",
  dob: "dob",
  prefix: "nameAdds",
  middle: "nameAdds",
  suffix: "nameAdds",
  gender: "gender",
  ethnicity: "demographicsMain",
  language: "demographicsMain",
  school: "school",
  sailingExp: "sailingExp",
};
const fields = computed<FieldOverrides<PersonMemberAccessor["fields"]>>(() =>
  Object.fromEntries(
    CreatePersonMemberMeta.properties.map((field) => {
      const key = field.propertyName;
      const section = layout.find((group) => group.fields.includes(key));
      return [
        key,
        {
          hidden:
            ["createdOn", "createdBy", "updatedOn", "updatedBy"].includes(
              key,
            ) ||
            (key === "organization" && !mask.value?.isOrganization) ||
            (props.tabbed && !tabFields.includes(key)) ||
            (!!fieldGroups[key] &&
              !mask.value?.visibleFields.includes(fieldGroups[key])) ||
            (!!section &&
              !["name", "contact"].includes(section.key) &&
              !mask.value?.visibleSections.includes(section.key)),
          label: labels[key],
          readonly:
            key === "email" && props.registration && props.email != null,
          meta: {
            ...field,
            required: !!mask.value?.requiredFields.includes(key),
          },
        },
      ];
    }),
  ),
);
const emailField = computed(() => {
  const field = fields.value.email;
  const meta = field?.meta;
  if (!meta) return undefined;
  return {
    meta,
    label: field.label ?? fieldLabel("email"),
    readonly: field.readonly,
  };
});
function updateEmail(value: unknown) {
  if (!accessor.value || blocked.value || emailField.value?.readonly) return;
  writeField(accessor.value.fields, "email", value);
  triggerRef(accessor);
}

function setProfile(profile: MemberProfile) {
  original.value = profile;
  accessor.value = PersonMemberAccessor.forUpdate(profile.person);
  phones.value = profile.phones.map(phoneRow);
  addresses.value = profile.addresses.map(addressRow);
  contacts.value = profile.emergencyContacts;
  selectedContacts.value = contacts.value.map((p) => p.personId);
  contactDrafts.value = Object.fromEntries(
    profile.emergencyContactDetails.map((c) => [
      c.person.personId,
      {
        person: {
          first: c.person.first,
          last: c.person.last,
          email: c.person.email,
        },
        phones: c.phones.map(phoneRow),
        addresses: c.addresses.map(addressRow),
        editableInPlace: c.editableInPlace,
      },
    ]),
  );
  newContacts.value = [];
}
function phoneRow(
  p: MemberProfile["phones"][number],
): MemberProfilePhoneChange {
  return {
    association: p.association,
    phone: { phoneNumber: p.phone.phoneNumber, phoneType: p.phone.phoneType },
    name: p.name,
    primary: p.primary,
    remove: false,
  };
}
function addressRow(
  a: MemberProfile["addresses"][number],
): MemberProfileAddressChange {
  return {
    association: a.association,
    address: {
      line1: a.address.line1,
      line2: a.address.line2,
      line3: a.address.line3,
      city: a.address.city,
      state: a.address.state,
      country: a.address.country,
      zip: a.address.zip,
    },
    name: a.name,
    primary: a.primary,
    remove: false,
  };
}

function blankPhoneRow(): MemberProfilePhoneChange {
  return {
    association: null,
    phone: { phoneNumber: "", phoneType: null },
    name: "Primary",
    primary: true,
    remove: false,
  };
}
function blankAddressRow(): MemberProfileAddressChange {
  return {
    association: null,
    address: {
      line1: "",
      line2: null,
      line3: null,
      city: "",
      state: USStateCode.MA,
      country: CountryCode.US,
      zip: "",
    },
    name: "Home",
    primary: true,
    remove: false,
  };
}
// Registration always sends a primary phone and address so the server's errors
// for them land on a visible row.
function ensurePrimaryRow<
  T extends { readonly primary: boolean | null; readonly remove: boolean },
>(rows: T[], blank: () => T): T[] {
  if (rows.some((row) => !row.remove && row.primary)) return rows;
  const first = rows.findIndex((row) => !row.remove);
  if (first < 0) return [...rows, blank()];
  return rows.map((row, i) => (i === first ? { ...row, primary: true } : row));
}

async function load() {
  const current = ++version;
  loading.value = true;
  saving.value = false;
  errors.value = {};
  loadError.value = "";
  saveError.value = "";
  accessor.value = undefined;
  original.value = undefined;
  phones.value = [];
  addresses.value = [];
  contacts.value = [];
  contactDrafts.value = {};
  newContacts.value = [];
  selectedContacts.value = [];
  household.value = [];
  householdAddresses.value = [];
  try {
    const [definitions, profile] = await Promise.all([
      rules.masksMember(),
      props.personId == null
        ? Promise.resolve(undefined)
        : service.getProfileMember({ personId: props.personId }),
    ]);
    if (current !== version) return;
    masks.value = definitions;
    if (profile) setProfile(profile);
    else
      accessor.value = PersonMemberAccessor.forCreate({ email: props.email });
    if (
      props.email != null &&
      accessor.value &&
      accessor.value.fields.email !== props.email
    )
      accessor.value.fields.email = props.email;
    if (
      !phones.value.length &&
      (props.registration || (mask.value?.minPhones ?? 0) > 0)
    )
      phones.value.push(blankPhoneRow());
    if (
      !addresses.value.length &&
      (props.registration || (mask.value?.minAddresses ?? 0) > 0)
    )
      addresses.value.push(blankAddressRow());
    if (!props.registration) {
      const id = Number(auth.accessToken?.claims.sub);
      const result = await service.getPersonMember(id);
      const [parentContacts, children] = await Promise.all([
        result.getEmergencyContacts(),
        result.getChildren(),
      ]);
      if (current !== version) return;
      // Children may use the parent or the parent's contacts, but no child is selectable.
      const childIds = new Set(children.map((c) => c.personId));
      household.value = [result, ...parentContacts].filter(
        (p) => !childIds.has(p.personId),
      );
      if (props.child) {
        const home = await service.getProfileMember({ personId: id });
        if (current !== version) return;
        householdAddresses.value = [...home.addresses]
          .sort((a, b) => Number(b.primary) - Number(a.primary))
          .flatMap(({ name, address }) =>
            address
              ? [
                  {
                    name: name || "Home",
                    address: {
                      line1: address.line1,
                      line2: address.line2,
                      line3: address.line3,
                      city: address.city,
                      state: address.state,
                      country: address.country,
                      zip: address.zip,
                    },
                  },
                ]
              : [],
          );
      }
    } else household.value = [];
  } catch (cause) {
    if (current === version) loadError.value = errorMessage(cause);
  } finally {
    if (current === version) loading.value = false;
  }
}
watch(
  [
    () => props.personId,
    () => props.child,
    () => auth.isAuthenticated(),
    () => auth.accessToken?.claims.sub,
  ],
  () => {
    if (auth.isAuthenticated()) void load();
    else {
      ++version;
      accessor.value = undefined;
      original.value = undefined;
      household.value = [];
      contacts.value = [];
      contactDrafts.value = {};
      newContacts.value = [];
      phones.value = [];
      addresses.value = [];
      errors.value = {};
      loading.value = false;
      saving.value = false;
    }
  },
  { immediate: true, flush: "sync" },
);
watch(
  () => props.email,
  (email) => {
    if (email != null && accessor.value) {
      accessor.value.fields.email = email;
      triggerRef(accessor);
    }
  },
);

const selectableContacts = computed(() => [
  ...new Map(
    [...household.value, ...contacts.value]
      .filter((p) => p.personId !== props.personId)
      .map((p) => [p.personId, p]),
  ).values(),
]);
const unselectedContacts = computed(() =>
  selectableContacts.value.filter(
    (p) => !selectedContacts.value.includes(p.personId),
  ),
);
function contactName(personId: number) {
  return (
    selectableContacts.value.find((p) => p.personId === personId)?.name ??
    `Contact #${personId}`
  );
}
function selectContact(personId: number) {
  if (!selectedContacts.value.includes(personId))
    selectedContacts.value = [...selectedContacts.value, personId];
}
function addEmergencyContact(auto = false) {
  const contact: MemberEmergencyContactCreate = {
    person: {
      ...emptySearchParams<MemberEmergencyContactCreate["person"]>(
        CreatePersonMemberMeta,
      ),
    },
    phones: [
      {
        association: null,
        phone: { phoneNumber: "", phoneType: null },
        name: "Primary",
        primary: true,
        remove: false,
      },
    ],
    addresses: [],
  };
  if (auto) autoContacts.add(contact);
  newContacts.value.push(contact);
}
const minimumContacts = computed(() =>
  Math.max(props.minEmergencyContacts, mask.value?.minEmergencyContacts ?? 0),
);
const contactCount = computed(
  () => selectedContacts.value.length + newContacts.value.length,
);
// Placeholder contacts added to reach the mask minimum; removed again while blank
// once the minimum drops or existing contacts are selected instead.
const autoContacts = new WeakSet<object>();
function blankContact(contact: MemberEmergencyContactCreate) {
  return (
    !contact.person.first?.trim() &&
    !contact.person.last?.trim() &&
    !contact.person.email?.trim() &&
    !contact.addresses.length &&
    contact.phones.every((p) => !p.phone?.phoneNumber?.trim())
  );
}
function syncEmergencyContacts(trim: boolean) {
  if (!accessor.value || loading.value) return;
  while (contactCount.value < minimumContacts.value) addEmergencyContact(true);
  if (!trim) return;
  for (
    let i = newContacts.value.length - 1;
    i >= 0 && contactCount.value > minimumContacts.value;
    i--
  ) {
    const contact = newContacts.value[i];
    if (autoContacts.has(toRaw(contact)) && blankContact(contact))
      newContacts.value.splice(i, 1);
  }
}
// Untouched auto-added blanks are dropped when the minimum falls or a household
// contact is selected, but not when the user adds another contact themselves.
watch(
  [minimumContacts, accessor, loading, () => selectedContacts.value.length],
  () => syncEmergencyContacts(true),
);
watch(
  () => newContacts.value.length,
  () => syncEmergencyContacts(false),
);
function addressKey(address: MemberProfileAddressChange["address"]) {
  return [
    address?.line1,
    address?.line2,
    address?.line3,
    address?.city,
    address?.state,
    address?.zip,
    address?.country,
  ]
    .map((v) => (v ?? "").toString().trim().toLowerCase())
    .join("|");
}
// Household addresses not already on this profile, offered as one-click chips.
const availableHouseholdAddresses = computed(() => {
  const used = new Set(
    addresses.value.filter((a) => !a.remove).map((a) => addressKey(a.address)),
  );
  return householdAddresses.value.filter(
    (h) => !used.has(addressKey(h.address)),
  );
});
function householdAddressLabel({ name, address }: HouseholdAddress) {
  const where = [address.line1, address.city].filter(Boolean).join(", ");
  return where ? `${name}: ${where}` : name;
}
function useHouseholdAddress(household: HouseholdAddress) {
  const address = {
    association: null,
    address: { ...household.address },
    name: household.name,
    primary: true,
    remove: false,
  };
  if (
    addresses.value.length === 1 &&
    !addresses.value[0].association &&
    !addresses.value[0].address?.line1
  )
    addresses.value = [address];
  else {
    addresses.value = [
      ...addresses.value.map((a) => ({ ...a, primary: false })),
      address,
    ];
  }
}
const emergencyFields = CreatePersonMemberMeta.properties.filter((p) =>
  ["first", "last", "email"].includes(p.propertyName),
);
function phoneChanges(
  rows = phones.value,
  originals: MemberProfile["phones"] = original.value?.phones ?? [],
) {
  return rows
    .filter((row) => {
      if (!row.association || row.remove) return true;
      const old = originals.find(
        (p) => p.association.assignId === row.association?.assignId,
      );
      return (
        !old ||
        row.phone?.phoneNumber !== old.phone.phoneNumber ||
        row.phone.phoneType !== old.phone.phoneType ||
        row.name !== old.name ||
        row.primary !== old.primary
      );
    })
    .map((row) =>
      row.remove ? { ...row, phone: null, primary: false } : { ...row },
    );
}
function addressChanges(
  rows = addresses.value,
  originals: MemberProfile["addresses"] = original.value?.addresses ?? [],
) {
  return rows
    .filter((row) => {
      if (!row.association || row.remove) return true;
      const old = originals.find(
        (a) => a.association.assignId === row.association?.assignId,
      );
      return (
        !old ||
        CreateAddressMemberFields.some(
          (key) =>
            readField(row.address ?? {}, key) !== readField(old.address, key),
        ) ||
        row.name !== old.name ||
        row.primary !== old.primary
      );
    })
    .map((row) =>
      row.remove ? { ...row, address: null, primary: false } : { ...row },
    );
}
const CreateAddressMemberFields = [
  "line1",
  "line2",
  "line3",
  "city",
  "state",
  "country",
  "zip",
];
function rowIndexes<T extends { association?: { assignId: number } | null }>(
  changed: T[],
  rows: T[],
  value: (row: T) => unknown,
) {
  return changed.map((row) =>
    rows.findIndex((r) =>
      row.association
        ? r.association?.assignId === row.association.assignId
        : value(r) === value(row),
    ),
  );
}
function contactEdits() {
  return selectedContacts.value.flatMap((personId) => {
    const draft = contactDrafts.value[personId];
    const old = original.value?.emergencyContactDetails.find(
      (c) => c.person.personId === personId,
    );
    if (!draft || !old) return [];
    const changedPhones = phoneChanges(draft.phones, old.phones);
    const changedAddresses = addressChanges(draft.addresses, old.addresses);
    const text = (value: string | null | undefined) => value?.trim() || null;
    if (
      text(draft.person.first) === text(old.person.first) &&
      text(draft.person.last) === text(old.person.last) &&
      text(draft.person.email) === text(old.person.email) &&
      !changedPhones.length &&
      !changedAddresses.length
    )
      return [];
    return [
      {
        personId,
        // Server errors index the sent rows; map them back to the drafted rows.
        phoneIndexes: rowIndexes(changedPhones, draft.phones, (r) => r.phone),
        addressIndexes: rowIndexes(
          changedAddresses,
          draft.addresses,
          (r) => r.address,
        ),
        update: {
          contact: { personId },
          person: {
            ...emptySearchParams<MemberEmergencyContactCreate["person"]>(
              CreatePersonMemberMeta,
            ),
            first: draft.person.first,
            last: draft.person.last,
            email: draft.person.email,
          },
          phones: changedPhones,
          addresses: changedAddresses,
        } satisfies MemberEmergencyContactUpdate,
      },
    ];
  });
}
function contactErrors(prefix: string) {
  return Object.fromEntries(
    Object.entries(errors.value)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => [key.slice(prefix.length), value]),
  );
}

async function save() {
  if (blocked.value || !accessor.value || underAge.value) return;
  const current = version;
  const subject = auth.accessToken?.claims.sub;
  const completing = props.registration;
  saving.value = true;
  saveError.value = "";
  errors.value = {};
  accessor.value.fieldErrors = {};
  if (completing) {
    phones.value = ensurePrimaryRow(phones.value, blankPhoneRow);
    addresses.value = ensurePrimaryRow(addresses.value, blankAddressRow);
  }
  const changedPhones = phoneChanges();
  const changedAddresses = addressChanges();
  const phoneIndexes = changedPhones.map((row) =>
    phones.value.findIndex((r) =>
      row.association
        ? r.association?.assignId === row.association.assignId
        : r.phone === row.phone,
    ),
  );
  const addressIndexes = changedAddresses.map((row) =>
    addresses.value.findIndex((r) =>
      row.association
        ? r.association?.assignId === row.association.assignId
        : r.address === row.address,
    ),
  );
  const contactIds = selectedContacts.value.map((personId) => ({ personId }));
  const edits = contactEdits();
  const sameContacts =
    JSON.stringify([...selectedContacts.value].sort()) ===
    JSON.stringify(
      (original.value?.emergencyContacts.map((p) => p.personId) ?? []).sort(),
    );
  let saved: MemberProfile | undefined;
  let registrationCompleted = false;
  let identityPhoneIndex: number | undefined;
  let identityAddressIndex: number | undefined;
  try {
    await accessor.value.save({
      create: async (person) => {
        saved = await service.createChildMember({
          person,
          phones: changedPhones,
          addresses: changedAddresses,
          emergencyContacts: contactIds,
          newEmergencyContacts: newContacts.value,
          updatedEmergencyContacts: [],
        });
        return saved.person;
      },
      update: async (person) => {
        const request: MemberProfileUpdate = {
          person,
          phones: changedPhones,
          addresses: changedAddresses,
          emergencyContacts: sameContacts ? null : contactIds,
          newEmergencyContacts: newContacts.value,
          updatedEmergencyContacts: edits.map((edit) => edit.update),
        };
        if (props.registration) {
          const phoneIndex = changedPhones.findIndex(
            (p) => !p.remove && p.primary,
          );
          const addressIndex = changedAddresses.findIndex(
            (a) => !a.remove && a.primary,
          );
          // An unchanged saved primary isn't in the change list; send it as-is.
          const phoneRowIndex =
            phoneIndex >= 0
              ? phoneIndexes[phoneIndex]
              : phones.value.findIndex((p) => !p.remove && p.primary);
          const addressRowIndex =
            addressIndex >= 0
              ? addressIndexes[addressIndex]
              : addresses.value.findIndex((a) => !a.remove && a.primary);
          const phone = phones.value[phoneRowIndex].phone!;
          const address = addresses.value[addressRowIndex].address!;
          const values = accessor.value!.fields;
          identityPhoneIndex = phoneRowIndex;
          identityAddressIndex = addressRowIndex;
          if (phoneIndex >= 0) phoneIndexes.splice(phoneIndex, 1);
          if (addressIndex >= 0) addressIndexes.splice(addressIndex, 1);
          await auth.completeProtoPerson(
            {
              first: (values.first ?? "").trim(),
              last: (values.last ?? "").trim(),
              email: (values.email ?? "").trim(),
              phone,
              address,
            },
            {
              ...request,
              phones: changedPhones.filter((_, i) => i !== phoneIndex),
              addresses: changedAddresses.filter((_, i) => i !== addressIndex),
            },
          );
          registrationCompleted = true;
          saved = await service.getProfileMember({ personId: person.personId });
        } else saved = await service.updateProfileMember(request);
        return saved.person;
      },
    });
    if (
      current === version &&
      subject === auth.accessToken?.claims.sub &&
      saved
    ) {
      setProfile(saved);
      emit("saved", saved);
    }
  } catch (cause) {
    if (current === version) {
      if (
        registrationCompleted ||
        (completing &&
          !auth.isProtoMember &&
          subject === auth.accessToken?.claims.sub)
      ) {
        loadError.value = `Registration was saved, but refreshing the profile failed: ${errorMessage(cause)}`;
        accessor.value = undefined;
        return;
      }
      const rawMsg = errorMessage(cause);
      const fieldErrors = validationFieldErrors(cause);
      const hasFieldLevelErrors = Object.keys(fieldErrors).length > 0;
      saveError.value = hasFieldLevelErrors
        ? "Please review and complete the highlighted required fields below."
        : rawMsg;
      errors.value = Object.fromEntries(
        Object.entries(fieldErrors).map(([key, message]) => {
          if (
            identityPhoneIndex != null &&
            ["phoneNumber", "phoneType"].includes(key)
          )
            return [`phones.${identityPhoneIndex}.${key}`, message];
          if (
            identityAddressIndex != null &&
            CreateAddressMemberFields.includes(key)
          )
            return [`addresses.${identityAddressIndex}.${key}`, message];
          const edited =
            /^updatedEmergencyContacts\.(\d+)(?:\.(phones|addresses)\.(\d+))?(\..*)?$/.exec(
              key,
            );
          if (edited) {
            const edit = edits[Number(edited[1])];
            if (!edit) return [key, message];
            const prefix = `updatedEmergencyContacts.${edit.personId}`;
            if (!edited[2]) return [`${prefix}${edited[4] ?? ""}`, message];
            const rows =
              edited[2] === "phones" ? edit.phoneIndexes : edit.addressIndexes;
            return [
              `${prefix}.${edited[2]}.${rows[Number(edited[3])] ?? edited[3]}${edited[4] ?? ""}`,
              message,
            ];
          }
          const match = /^(phones|addresses)\.(\d+)\.(.*)$/.exec(key);
          if (!match) return [key, message];
          const indexes = match[1] === "phones" ? phoneIndexes : addressIndexes;
          const index = indexes[Number(match[2])];
          return [
            index == null || index < 0
              ? key
              : `${match[1]}.${index}.${match[3]}`,
            message,
          ];
        }),
      );
      accessor.value!.fieldErrors = errors.value;
      triggerRef(accessor);
      const failed = (
        ["info", "contact", "address", "emergency"] as ProfileTab[]
      ).find((key) => tabErrors.value[key]);
      if (props.tabbed && failed) tab.value = failed;
    }
  } finally {
    if (current === version) saving.value = false;
  }
}
</script>

<template>
  <v-progress-linear v-if="loading" indeterminate />
  <v-alert v-if="loadError" type="error" role="alert">
    {{ loadError }} <v-btn @click="load">Retry</v-btn>
  </v-alert>
  <form
    v-else-if="accessor && !loading"
    @submit.prevent="save"
    data-testid="member-profile-form"
  >
    <v-alert v-if="saveError" type="error" role="alert" class="mb-4">{{
      saveError
    }}</v-alert>
    <v-alert
      v-if="underAge"
      type="warning"
      role="alert"
      class="mb-4"
      data-testid="under-age-alert"
      >{{ underAgeMessage }}</v-alert
    >

    <div
      v-if="
        !tabbed &&
        (assignedPersonTypes.length ||
          eligiblePersonTypes.length ||
          displayedMasks.length)
      "
      class="mb-4 d-flex flex-wrap gap-2 align-center"
      data-testid="person-type-badges"
    >
      <div
        v-if="assignedPersonTypes.length"
        class="d-flex flex-wrap align-center mr-4"
      >
        <span class="text-caption text-medium-emphasis mr-2">Assigned:</span>
        <v-chip
          v-for="typeKey in assignedPersonTypes"
          :key="'assigned-' + typeKey"
          color="primary"
          size="small"
          class="mr-1 mb-1"
          data-testid="assigned-type-chip"
        >
          {{ PERSON_TYPE_LABELS[typeKey] ?? typeKey }}
        </v-chip>
      </div>

      <div
        v-if="eligiblePersonTypes.length"
        class="d-flex flex-wrap align-center mr-4"
      >
        <span class="text-caption text-medium-emphasis mr-2">Eligible:</span>
        <v-chip
          v-for="typeKey in eligiblePersonTypes"
          :key="'eligible-' + typeKey"
          color="success"
          variant="outlined"
          size="small"
          class="mr-1 mb-1"
          data-testid="eligible-type-chip"
        >
          {{ PERSON_TYPE_LABELS[typeKey] ?? typeKey }}
        </v-chip>
      </div>

      <div class="d-flex flex-wrap align-center">
        <span class="text-caption text-medium-emphasis mr-2"
          >Check requirements:</span
        >
        <v-chip
          v-for="m in displayedMasks"
          :key="'target-' + m.personType"
          :color="selectedTargetType === m.personType ? 'secondary' : undefined"
          :variant="selectedTargetType === m.personType ? 'flat' : 'outlined'"
          size="small"
          class="mr-1 mb-1 cursor-pointer"
          data-testid="target-role-chip"
          @click="selectTargetType(m.personType)"
        >
          <v-icon
            v-if="selectedTargetType === m.personType"
            start
            icon="mdi-check"
            size="small"
          />
          {{ PERSON_TYPE_LABELS[m.personType] ?? m.personType }}
        </v-chip>
      </div>
    </div>

    <v-tabs v-if="tabbed" v-model="tab" class="mb-4" data-testid="profile-tabs">
      <v-tab value="info"
        >{{ tabTitle }}
        <v-badge
          v-if="tabErrors.info"
          inline
          color="error"
          :content="tabErrors.info"
      /></v-tab>
      <v-tab value="contact"
        >Contact Information
        <v-badge
          v-if="tabErrors.contact"
          inline
          color="error"
          :content="tabErrors.contact"
      /></v-tab>
      <v-tab v-if="showAddressTab" value="address"
        >Address
        <v-badge
          v-if="tabErrors.address"
          inline
          color="error"
          :content="tabErrors.address"
      /></v-tab>
      <v-tab v-if="showEmergencyTab" value="emergency"
        >Emergency Contacts
        <v-badge
          v-if="tabErrors.emergency"
          inline
          color="error"
          :content="tabErrors.emergency"
      /></v-tab>
    </v-tabs>
    <div>
      <div v-show="!tabbed || tab === 'info'">
        <GeneratedCrudForm
          :accessor="accessor"
          :meta="personId ? UpdatePersonMemberMeta : CreatePersonMemberMeta"
          :fields="fields"
          :layout="tabbed ? tabLayout : layout"
          :disabled="blocked"
          embedded
        >
          <template #field="{ field, value, update, error }">
            <v-date-input
              v-if="field.key === 'dob'"
              :model-value="value instanceof Date ? value : null"
              :label="field.meta.required ? 'Date of birth *' : 'Date of birth'"
              :disabled="blocked"
              :error-messages="error"
              @update:model-value="
                update($event);
                dobDraft = $event ?? null;
              "
            />
            <GeneratedFieldControl
              v-else
              :meta="field.meta"
              :label="field.label"
              :model-value="value"
              :disabled="blocked"
              :readonly="field.readonly"
              :error="error"
              @update:model-value="update"
            />
          </template>
        </GeneratedCrudForm>
      </div>
      <div v-show="!tabbed || tab === 'contact'">
        <GeneratedFieldControl
          v-if="tabbed && emailField"
          :meta="emailField.meta"
          :label="emailField.label"
          :model-value="accessor.fields.email"
          :disabled="blocked"
          :readonly="emailField.readonly"
          :error="errors.email"
          @update:model-value="updateEmail"
        />
        <MemberProfileContacts
          v-model:phones="phones"
          v-model:addresses="addresses"
          :show="tabbed ? ['phones'] : ['phones', 'addresses']"
          :disabled="blocked"
          :errors="errors"
        />
      </div>
      <div
        v-if="!tabbed || showAddressTab"
        v-show="!tabbed || tab === 'address'"
      >
        <MemberProfileContacts
          v-if="tabbed"
          v-model:phones="phones"
          v-model:addresses="addresses"
          :show="['addresses']"
          :disabled="blocked"
          :errors="errors"
        />
        <div
          v-if="availableHouseholdAddresses.length"
          class="mt-3"
          data-testid="household-addresses"
        >
          <span class="text-body-2 mr-2">Use existing address:</span>
          <v-chip
            v-for="h in availableHouseholdAddresses"
            :key="addressKey(h.address)"
            :disabled="blocked"
            class="mr-1 mb-1"
            append-icon="mdi-plus"
            data-testid="household-address-chip"
            @click="useHouseholdAddress(h)"
            >{{ householdAddressLabel(h) }}</v-chip
          >
        </div>
      </div>
      <div
        v-if="!tabbed || showEmergencyTab"
        v-show="!tabbed || tab === 'emergency'"
      >
        <div class="d-flex align-center justify-space-between mt-6">
          <h3>Emergency contacts</h3>
          <CreateButton
            label="Add emergency contact"
            :disabled="blocked"
            @click="addEmergencyContact()"
          />
        </div>
        <p
          v-if="minimumContacts > 0"
          class="text-body-2 text-medium-emphasis"
          data-testid="emergency-contact-minimum"
        >
          At least {{ minimumContacts }} emergency contacts are required.
        </p>
        <div
          v-if="unselectedContacts.length"
          class="mb-3"
          data-testid="existing-contact-chips"
        >
          <span class="text-body-2 mr-2">Use existing person:</span>
          <v-chip
            v-for="contact in unselectedContacts"
            :key="contact.personId"
            :disabled="blocked"
            class="mr-1 mb-1"
            append-icon="mdi-plus"
            data-testid="existing-contact-chip"
            @click="selectContact(contact.personId)"
            >{{ contact.name }}</v-chip
          >
        </div>
        <v-alert
          v-if="errors.emergencyContacts"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
          role="alert"
          >{{ errors.emergencyContacts }}</v-alert
        >
        <template v-for="personId in selectedContacts" :key="personId">
          <v-card
            v-if="contactDrafts[personId]"
            variant="outlined"
            class="pa-4 mb-3"
            data-testid="existing-emergency-contact"
          >
            <div
              v-if="contactCount > minimumContacts"
              class="d-flex justify-end"
            >
              <DeleteButton
                :label="`Remove emergency contact ${contactDrafts[personId].person.first ?? ''} ${contactDrafts[personId].person.last ?? ''}`"
                :disabled="blocked"
                @click="
                  selectedContacts = selectedContacts.filter(
                    (id) => id !== personId,
                  )
                "
              />
            </div>
            <p
              v-if="!contactDrafts[personId].editableInPlace"
              class="text-body-2 text-medium-emphasis mb-2"
              data-testid="emergency-contact-copy-note"
            >
              This contact is shared with another profile. Saving changes here
              creates a separate copy for this profile.
            </p>
            <GeneratedFieldControl
              v-for="field in emergencyFields"
              :key="field.propertyName"
              :meta="field"
              :label="
                labels[field.propertyName] ?? fieldLabel(field.propertyName)
              "
              :model-value="
                readField(contactDrafts[personId].person, field.propertyName)
              "
              :disabled="blocked"
              :error="
                errors[
                  `updatedEmergencyContacts.${personId}.${field.propertyName}`
                ]
              "
              @update:model-value="
                writeField(
                  contactDrafts[personId].person,
                  field.propertyName,
                  $event,
                )
              "
            />
            <MemberProfileContacts
              v-model:phones="contactDrafts[personId].phones"
              v-model:addresses="contactDrafts[personId].addresses"
              :disabled="blocked"
              :errors="contactErrors(`updatedEmergencyContacts.${personId}.`)"
            />
          </v-card>
          <v-card
            v-else
            variant="outlined"
            class="pa-4 mb-3 d-flex align-center"
            data-testid="existing-emergency-contact"
          >
            <span class="flex-grow-1">{{ contactName(personId) }}</span>
            <DeleteButton
              v-if="contactCount > minimumContacts"
              :label="`Remove emergency contact ${contactName(personId)}`"
              :disabled="blocked"
              @click="
                selectedContacts = selectedContacts.filter(
                  (id) => id !== personId,
                )
              "
            />
          </v-card>
        </template>
        <v-card
          v-for="(contact, index) in newContacts"
          :key="index"
          variant="outlined"
          class="pa-4 mb-3"
          data-testid="new-emergency-contact"
        >
          <div v-if="contactCount > minimumContacts" class="d-flex justify-end">
            <DeleteButton
              :label="`Remove emergency contact ${index + 1}`"
              :disabled="blocked"
              @click="newContacts.splice(index, 1)"
            />
          </div>
          <GeneratedFieldControl
            v-for="field in emergencyFields"
            :key="field.propertyName"
            :meta="field"
            :label="
              labels[field.propertyName] ?? fieldLabel(field.propertyName)
            "
            :model-value="readField(contact.person, field.propertyName)"
            :disabled="blocked"
            :error="
              errors[`newEmergencyContacts.${index}.${field.propertyName}`]
            "
            @update:model-value="
              writeField(contact.person, field.propertyName, $event)
            "
          />
          <MemberProfileContacts
            :phones="contact.phones"
            :addresses="contact.addresses"
            :disabled="blocked"
            :errors="
              Object.fromEntries(
                Object.entries(errors)
                  .filter(([key]) =>
                    key.startsWith(`newEmergencyContacts.${index}.`),
                  )
                  .map(([key, value]) => [
                    key.slice(`newEmergencyContacts.${index}.`.length),
                    value,
                  ]),
              )
            "
            @update:phones="writeField(contact, 'phones', $event)"
            @update:addresses="writeField(contact, 'addresses', $event)"
          />
        </v-card>
      </div>
    </div>
    <v-card-actions class="mt-4">
      <v-btn :disabled="blocked" @click="emit('cancel')">Cancel</v-btn>
      <v-spacer />
      <v-btn
        type="submit"
        color="primary"
        variant="flat"
        :loading="saving"
        :disabled="blocked || underAge"
        >{{ submitLabel }}</v-btn
      >
    </v-card-actions>
  </form>
</template>
